import React, { useCallback, useEffect, useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuCamera,
  LuMail,
  LuMapPin,
  LuPhone,
  LuUser,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchLoggingUserData,
  getLoggingUser,
} from "../../features/loggingUser/loggingUserSlice";
import { POSTFormData } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";

interface IFormInput {
  name: string;
  email: string;
  phone: string;
  address: string;
}

const UserProfileForm = () => {
  const dispatch = useAppDispatch();
  const loggingUser = useAppSelector(getLoggingUser);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({ mode: "onChange" });

  const handleFileChange = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setAvatarPreview(null);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleFileChange(event.dataTransfer.files);
    },
    [handleFileChange]
  );

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    try {
      await POSTFormData("/v1/profile", {
        name: data.name,
        email: data.email,
        address: data.address && data.address !== "" ? data.address : "",
        ...(selectedFile ? { image: selectedFile } : {}),
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await dispatch(fetchLoggingUserData());
            setSelectedFile(null);

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (loggingUser?.id) {
      reset({
        name: loggingUser.name || "",
        email: loggingUser.email || "",
        phone: loggingUser.phone || "",
        address: loggingUser.address || "",
      });

      setAvatarPreview(loggingUser.image ?? null);
      setSelectedFile(null);
    }
  }, [loggingUser, reset]);

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="flex flex-col items-start gap-4">
            <div className="relative group">
              <span className="relative flex h-28 w-28 shrink-0 overflow-hidden rounded-full border-2 border-[#34699A] shadow-md">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt={loggingUser?.name || ""}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center rounded-full bg-[#34699A] text-white text-3xl font-bold">
                    {loggingUser?.name?.substring(0, 2)?.toUpperCase() || ""}
                  </span>
                )}
              </span>

              <label
                htmlFor="image-upload"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="absolute inset-0 flex items-center justify-center bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => handleFileChange(event.target.files)}
                />
                <div className="flex flex-col items-center">
                  <LuCamera className="h-6 w-6 mb-1" />
                  <span className="text-sm font-medium">ફોટો બદલો</span>
                </div>
              </label>
            </div>

            {selectedFile && (
              <p className="text-sm text-[#113F67] mt-2">
                પસંદ કરેલી ફાઇલ: {selectedFile.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="userProfileForm-name"
                className="text-sm font-semibold text-[#113F67]"
              >
                યુઝરનેમ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuUser className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="userProfileForm-name"
                  {...register("name", {
                    required: "યુઝરનેમ આવશ્યક છે",
                    minLength: {
                      value: 3,
                      message: "યુઝરનેમ ઓછામાં ઓછો 3 અક્ષરનો હોવો જોઈએ",
                    },
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.name
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                {errors.name && (
                  <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.name && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="userProfileForm-email"
                className="text-sm font-semibold text-[#113F67]"
              >
                ઇમેઇલ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuMail className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="userProfileForm-email"
                  type="email"
                  {...register("email", {
                    required: "અમાન્ય ઇમેઇલ ફોર્મેટ",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "અમાન્ય ઇમેઇલ ફોર્મેટ",
                    },
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
              </div>

              {errors.email && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="userProfileForm-phoneNumber"
                className="text-sm font-semibold text-[#113F67]"
              >
                મોબાઇલ નંબર
              </label>

              <div className="relative">
                <LuPhone className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="userProfileForm-phoneNumber"
                  type="tel"
                  readOnly
                  {...register("phone")}
                  className="flex w-full px-3 py-2 focus-visible:outline-none pl-12 h-12 text-base border-2 border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="address"
              className="text-sm font-semibold text-[#113F67]"
            >
              સરનામું (વૈકલ્પિક)
            </label>

            <div className="relative">
              <LuMapPin className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
              <input
                id="address"
                {...register("address", {
                  maxLength: {
                    value: 200,
                    message: "સરનામું 200 અક્ષરથી વધુ લાંબુ ન હોવું જોઈએ",
                  },
                })}
                className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                  errors.address
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                }`}
              />
              {errors.address && (
                <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
              )}
            </div>

            {errors.address && (
              <p className="text-red-500 text-sm flex items-center mt-1">
                <LuAlertCircle className="h-4 w-4 mr-1" />
                {errors.address.message}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  સેવ થઈ રહ્યું છે...
                </div>
              ) : (
                "ફેરફારો સાચવો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default UserProfileForm;
